import { apiGet, apiSend } from './api.js';
import type {
  AutomationRule,
  AutomationRun,
  ComplianceControl,
  ComplianceEvidence,
  CorrectiveAction,
  Dpia,
  HealthBand,
  HealthScore,
  Incident,
  KillSwitchState,
  MetricValue,
  ProcessingActivity,
  Report,
  Risk,
  Vendor,
} from '@hospasense/contracts';

// Analytics
export async function fetchMetrics(): Promise<MetricValue[]> {
  const data = await apiGet<{ items: MetricValue[] }>('/api/v1/analytics/metrics');
  return data.items;
}

// Health
export async function fetchHealthScores(): Promise<HealthScore[]> {
  const data = await apiGet<{ items: HealthScore[] }>('/api/v1/health-scores');
  return data.items;
}
export async function refreshHealthScores(): Promise<number> {
  const data = await apiSend<{ refreshed: number }>('POST', '/api/v1/health-scores/refresh');
  return data.refreshed;
}
export async function reviewHealthScore(
  organisationId: string,
  band: HealthBand,
  note: string,
): Promise<void> {
  await apiSend('POST', `/api/v1/health-scores/${organisationId}/review`, { band, note });
}

// Compliance
export async function fetchComplianceControls(framework?: string): Promise<ComplianceControl[]> {
  const qs = framework ? `?framework=${encodeURIComponent(framework)}` : '';
  const data = await apiGet<{ items: ComplianceControl[] }>(`/api/v1/compliance/controls${qs}`);
  return data.items;
}
export async function fetchControlEvidence(controlId: string): Promise<ComplianceEvidence[]> {
  const data = await apiGet<{ items: ComplianceEvidence[] }>(
    `/api/v1/compliance/controls/${controlId}/evidence`,
  );
  return data.items;
}
export async function addControlEvidence(
  controlId: string,
  input: { title: string; reference: string; expiresAt?: string },
): Promise<void> {
  await apiSend('POST', `/api/v1/compliance/controls/${controlId}/evidence`, input);
}
export async function recordControlTest(
  controlId: string,
  input: { outcome: 'effective' | 'ineffective'; note: string; expectedVersion: number },
): Promise<void> {
  await apiSend('POST', `/api/v1/compliance/controls/${controlId}/test`, input);
}
export async function grantControlException(
  controlId: string,
  input: { reason: string; expiresAt: string; expectedVersion: number },
): Promise<void> {
  await apiSend('POST', `/api/v1/compliance/controls/${controlId}/exception`, input);
}
export async function downloadComplianceExport(): Promise<void> {
  const res = await fetch('/api/v1/compliance/export', { credentials: 'include' });
  if (!res.ok) throw new Error(`export failed with status ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'compliance-evidence.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// Risk, incidents, registers
export async function fetchRisks(): Promise<Risk[]> {
  return (await apiGet<{ items: Risk[] }>('/api/v1/risks')).items;
}
export async function createRisk(input: {
  title: string;
  description: string;
  likelihood: Risk['likelihood'];
  impact: Risk['impact'];
}): Promise<void> {
  await apiSend('POST', '/api/v1/risks', input);
}
export async function updateRiskStatus(
  id: string,
  input: { status: Risk['status']; acceptanceReason?: string; expectedVersion: number },
): Promise<void> {
  await apiSend('POST', `/api/v1/risks/${id}/status`, input);
}
export async function fetchIncidents(): Promise<Incident[]> {
  return (await apiGet<{ items: Incident[] }>('/api/v1/incidents')).items;
}
export async function transitionIncident(
  id: string,
  toStatus: string,
  expectedVersion: number,
): Promise<void> {
  await apiSend('POST', `/api/v1/incidents/${id}/transition`, { toStatus, expectedVersion });
}
export async function fetchCorrectiveActions(): Promise<CorrectiveAction[]> {
  return (await apiGet<{ items: CorrectiveAction[] }>('/api/v1/corrective-actions')).items;
}
export async function fetchVendors(): Promise<Vendor[]> {
  return (await apiGet<{ items: Vendor[] }>('/api/v1/vendors')).items;
}
export async function fetchProcessingActivities(): Promise<ProcessingActivity[]> {
  return (await apiGet<{ items: ProcessingActivity[] }>('/api/v1/processing-activities')).items;
}
export async function fetchDpias(): Promise<Dpia[]> {
  return (await apiGet<{ items: Dpia[] }>('/api/v1/dpias')).items;
}
export async function decideDpia(
  id: string,
  input: { decision: 'approved' | 'rejected'; assessment: string; expectedVersion: number },
): Promise<void> {
  await apiSend('POST', `/api/v1/dpias/${id}/decide`, input);
}

// Automation
export async function fetchAutomationRules(): Promise<AutomationRule[]> {
  return (await apiGet<{ items: AutomationRule[] }>('/api/v1/automation/rules')).items;
}
export async function createAutomationRule(input: {
  name: string;
  trigger: string;
  action: string;
  actionParams: Record<string, unknown>;
  maxRunsPerHour: number;
}): Promise<void> {
  await apiSend('POST', '/api/v1/automation/rules', input);
}
export async function setAutomationRuleEnabled(
  id: string,
  enabled: boolean,
  expectedVersion: number,
): Promise<void> {
  await apiSend('POST', `/api/v1/automation/rules/${id}/enabled`, { enabled, expectedVersion });
}
export async function fetchAutomationRuns(): Promise<AutomationRun[]> {
  return (await apiGet<{ items: AutomationRun[] }>('/api/v1/automation/runs')).items;
}
export interface DryRunRow {
  ruleId: string;
  ruleName: string;
  trigger: string;
  action: string;
  matches: number;
}
export async function fetchDryRun(): Promise<DryRunRow[]> {
  return (await apiGet<{ items: DryRunRow[] }>('/api/v1/automation/dry-run')).items;
}
export async function fetchKillSwitch(): Promise<KillSwitchState> {
  return apiGet<KillSwitchState>('/api/v1/automation/kill-switch');
}
export async function setKillSwitch(engaged: boolean, reason: string): Promise<KillSwitchState> {
  return apiSend<KillSwitchState>('POST', '/api/v1/automation/kill-switch', { engaged, reason });
}

// Reports
export type ReportListItem = Omit<Report, 'sections'>;
export async function fetchReports(): Promise<ReportListItem[]> {
  return (await apiGet<{ items: ReportListItem[] }>('/api/v1/reports')).items;
}
export async function fetchReport(id: string): Promise<Report> {
  return apiGet<Report>(`/api/v1/reports/${id}`);
}
export async function generateReport(kind: 'board_monthly' | 'operating_weekly'): Promise<string> {
  const data = await apiSend<{ id: string }>('POST', '/api/v1/reports/generate', { kind });
  return data.id;
}
